/**
 * Modules Service
 * Handles platform modules/entitlements API operations
 */

import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/config/api.config';

// Types
export interface Module {
  id: string;
  code: string;
  name: string;
  description: string;
  icon: string;
  category: 'core' | 'sales' | 'marketing' | 'analytics' | 'ai' | 'integrations' | 'admin';
  category_display: string;
  is_free: boolean;
  base_price: number;
  min_subscription_tier: 'free' | 'starter' | 'professional' | 'enterprise';
  tier_display: string;
  is_active: boolean;
  is_beta: boolean;
  requires_setup: boolean;
  display_order: number;
}

export interface ModulesByCategory {
  [category: string]: Array<{
    code: string;
    name: string;
    description: string;
    icon: string;
    is_beta: boolean;
    requires_setup: boolean;
  }>;
}

export interface CompanyEntitlements {
  company_id: string;
  company_name: string;
  is_platform: boolean;
  plan: string;
  enabled_modules: string[];
  modules_by_category: ModulesByCategory;
}

export interface ModuleCheckResult {
  results: Record<string, boolean>;
}

/**
 * Modules Service
 */
export const modulesService = {
  /**
   * Get all available modules
   */
  async getModules(params?: {
    category?: string;
    min_subscription_tier?: string;
    is_free?: boolean;
    is_beta?: boolean;
  }): Promise<Module[]> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = params
      ? `${API_ENDPOINTS.MODULES.BASE}?${queryParams.toString()}`
      : API_ENDPOINTS.MODULES.BASE;

    // The API returns paginated results
    const response = await apiClient.get<{ results: Module[] } | Module[]>(endpoint);
    return Array.isArray(response) ? response : response.results;
  },

  /**
   * Get module by ID
   */
  async getModuleById(id: string): Promise<Module> {
    return apiClient.get<Module>(API_ENDPOINTS.MODULES.DETAIL(id));
  },

  /**
   * Get modules grouped by category
   */
  async getModulesByCategory(): Promise<Record<string, Module[]>> {
    return apiClient.get<Record<string, Module[]>>(API_ENDPOINTS.MODULES.BY_CATEGORY);
  },

  /**
   * Get modules available for a subscription tier
   */
  async getModulesForTier(tier: string): Promise<Module[]> {
    return apiClient.get<Module[]>(`${API_ENDPOINTS.MODULES.FOR_TIER}?tier=${tier}`);
  },

  /**
   * Get current user's company entitlements
   */
  async getMyEntitlements(): Promise<CompanyEntitlements> {
    return apiClient.get<CompanyEntitlements>(API_ENDPOINTS.MODULES.MY_ENTITLEMENTS);
  },

  /**
   * Check if current user's company can use specific module(s)
   */
  async checkMyModules(moduleCodes: string[]): Promise<ModuleCheckResult> {
    return apiClient.post<ModuleCheckResult>(API_ENDPOINTS.MODULES.MY_ENTITLEMENTS, {
      module_codes: moduleCodes,
    });
  },

  /**
   * Check single module access for current user's company
   */
  async canUseModule(moduleCode: string): Promise<boolean> {
    const result = await this.checkMyModules([moduleCode]);
    return result.results[moduleCode] ?? false;
  },

  // ========== Company-specific endpoints (admin) ==========

  /**
   * Get entitlements for a specific company
   */
  async getCompanyEntitlements(companyId: string): Promise<CompanyEntitlements> {
    return apiClient.get<CompanyEntitlements>(API_ENDPOINTS.COMPANIES.ENTITLEMENTS(companyId));
  },

  /**
   * Check if a company can use specific module(s)
   */
  async checkCompanyModules(companyId: string, moduleCodes: string[]): Promise<ModuleCheckResult> {
    return apiClient.post<ModuleCheckResult>(API_ENDPOINTS.COMPANIES.CHECK_MODULE(companyId), {
      module_codes: moduleCodes,
    });
  },

  /**
   * Grant a module to a company (admin only)
   */
  async grantModule(companyId: string, moduleCode: string, options?: {
    usage_limit?: number;
    custom_price?: number;
    notes?: string;
  }): Promise<any> {
    return apiClient.post(API_ENDPOINTS.COMPANIES.GRANT_MODULE(companyId), {
      module_code: moduleCode,
      ...options,
    });
  },

  /**
   * Revoke a module from a company (admin only)
   */
  async revokeModule(companyId: string, moduleCode: string): Promise<{ status: string }> {
    return apiClient.post<{ status: string }>(API_ENDPOINTS.COMPANIES.REVOKE_MODULE(companyId), {
      module_code: moduleCode,
    });
  },
};
